/**
 * Sistema de notificaciones del navegador (Push Notifications)
 * Muestra notificaciones nativas del sistema operativo
 */

/**
 * Verifica si el navegador soporta notificaciones
 */
export function isNotificationSupported() {
  return 'Notification' in window;
}

/**
 * Obtiene el estado actual del permiso de notificaciones
 * @returns {'granted' | 'denied' | 'default'}
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) return 'denied';
  return Notification.permission;
}

/**
 * Solicita permiso para mostrar notificaciones
 * @returns {Promise<'granted' | 'denied' | 'default'>}
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    console.warn('Este navegador no soporta notificaciones');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error al solicitar permiso de notificaciones:', error);
    return 'denied';
  }
}

/**
 * Muestra una notificación del navegador
 * @param {Object} options - Opciones de la notificación
 * @param {string} options.title - Título de la notificación
 * @param {string} options.body - Cuerpo del mensaje
 * @param {string} options.icon - URL del icono (opcional)
 * @param {string} options.badge - URL del badge (opcional)
 * @param {string} options.tag - Tag único para agrupar notificaciones
 * @param {boolean} options.requireInteraction - Si requiere interacción del usuario
 * @param {Function} options.onClick - Callback al hacer click
 * @returns {Notification | null}
 */
export function showBrowserNotification(options) {
  const {
    title,
    body,
    icon = '/logo.png', // Puedes cambiar esto por tu logo
    badge,
    tag,
    requireInteraction = false,
    onClick,
  } = options;

  // Verificar soporte
  if (!isNotificationSupported()) {
    console.warn('Notificaciones no soportadas');
    return null;
  }

  // Verificar permiso
  if (Notification.permission !== 'granted') {
    console.warn('No hay permiso para mostrar notificaciones');
    return null;
  }

  try {
    const notification = new Notification(title, {
      body,
      icon,
      badge,
      tag,
      requireInteraction,
      silent: false, // Reproducir sonido del sistema
    });

    // Manejar click en la notificación
    if (onClick) {
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus(); // Enfocar la ventana
        onClick(event);
        notification.close();
      };
    }

    // Auto-cerrar después de 10 segundos si no requiere interacción
    if (!requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 10000);
    }

    return notification;
  } catch (error) {
    console.error('Error al mostrar notificación:', error);
    return null;
  }
}

/**
 * Muestra notificación de nuevo pedido
 * Funciona incluso con la app en segundo plano gracias al Service Worker
 * @param {Object} order - Datos del pedido
 */
export function showNewOrderNotification(order) {
  const title = '🛒 Nuevo Pedido Recibido';
  const body = `Pedido #${order.id} - ${order.customer_name}\nTotal: ${order.total_display}`;
  const icon = '/icon-192x192.png';
  const badge = '/icon-192x192.png';
  const tag = `order-${order.id}`;
  const data = {
    url: `/panel/pedidos/${order.id}`,
    orderId: order.id
  };

  // Si hay Service Worker activo, usarlo para notificaciones en segundo plano
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SHOW_NOTIFICATION',
      notification: { title, body, icon, badge, tag, data }
    });
  } else {
    // Fallback: Usar la función existente
    return showBrowserNotification({
      title,
      body,
      icon,
      badge,
      tag,
      requireInteraction: true,
      onClick: () => {
        window.location.href = `/panel/pedidos/${order.id}`;
      },
    });
  }
}

/**
 * Muestra notificación de cambio de estado de pedido
 * @param {Object} order - Datos del pedido
 */
export function showOrderStatusNotification(order) {
  const statusText = {
    pending_payment_confirmation: 'Pendiente de Confirmación',
    payment_confirmed: 'Pago Confirmado',
    ready_for_pickup: 'Listo para Retiro',
    pending_delivery: 'Pendiente de Envío',
    out_for_delivery: 'En Camino',
    delivered_confirmed: 'Entregado',
    cancelled: 'Cancelado',
  };

  return showBrowserNotification({
    title: '📦 Actualización de Pedido',
    body: `Pedido #${order.id}\nEstado: ${statusText[order.status] || order.status}`,
    tag: `order-status-${order.id}`,
    onClick: () => {
      window.location.href = `/panel/pedidos/${order.id}`;
    },
  });
}

/**
 * Muestra notificación genérica
 * Funciona incluso con la app en segundo plano gracias al Service Worker
 * @param {Object} notification - Datos de la notificación
 */
export function showGenericNotification(notification) {
  // Verificar permiso
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    console.warn('Notificaciones del navegador no disponibles');
    return null;
  }

  // MAPEO PARA SUPERADMIN: Detectar tipos de notificaciones de SuperAdmin
  const superAdminTypes = {
    'new_store_registered': { emoji: '🏪', title: 'Nueva Tienda Registrada', requireInteraction: true },
    'trial_expiring': { emoji: '⏰', title: 'Trial por Vencer', requireInteraction: true },
    'trial_expired': { emoji: '❌', title: 'Trial Finalizado', requireInteraction: true },
    'subscription_expiring': { emoji: '⚠️', title: 'Suscripción por Vencer', requireInteraction: true },
    'subscription_expired': { emoji: '🛑', title: 'Suscripción Expirada', requireInteraction: true },
    'subscription_payment_uploaded': { emoji: '💳', title: 'Nuevo Comprobante de Pago', requireInteraction: true },
    'subscription_activated': { emoji: '✅', title: 'Suscripción Activada', requireInteraction: false },
    'subscription_rejected': { emoji: '❌', title: 'Pago Rechazado', requireInteraction: true }
  };

  // Si es una notificación de SuperAdmin, usar mapeo específico
  if (superAdminTypes[notification.type]) {
    const config = superAdminTypes[notification.type];
    const title = `${config.emoji} ${config.title}`;
    const body = notification.message;
    const icon = '/icon-192x192.png';
    const badge = '/icon-192x192.png';
    const tag = `superadmin-${notification.type}-${notification.id}`;

    try {
      // Usar Notification API directamente para SuperAdmin
      const browserNotif = new Notification(title, {
        body,
        icon,
        badge,
        tag,
        requireInteraction: config.requireInteraction,
        silent: false,
      });

      // Manejar click
      browserNotif.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (notification.link) {
          window.location.href = notification.link;
        }
        browserNotif.close();
      };

      // Auto-cerrar si no requiere interacción
      if (!config.requireInteraction) {
        setTimeout(() => {
          browserNotif.close();
        }, 10000);
      }

      return browserNotif;
    } catch (error) {
      console.error('Error mostrando notificación del navegador:', error);
      return null;
    }
  }

  // MAPEO PARA TIENDA: Notificaciones normales de tienda
  const typeIcons = {
    order: '🛒',
    payment: '💰',
    delivery: '📦',
    system: '🔔',
    warning: '⚠️',
    success: '✅',
  };

  const icon = typeIcons[notification.type] || '🔔';
  const title = `${icon} ${notification.title}`;
  const body = notification.message;
  const notifIcon = '/icon-192x192.png';
  const badge = '/icon-192x192.png';
  const tag = `notification-${notification.id}`;
  const data = {
    url: notification.link || '/panel',
    notificationId: notification.id
  };

  // Si hay Service Worker activo, usarlo para notificaciones en segundo plano
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SHOW_NOTIFICATION',
      notification: { title, body, icon: notifIcon, badge, tag, data }
    });
  } else {
    // Fallback: Usar la función existente
    return showBrowserNotification({
      title,
      body,
      icon: notifIcon,
      badge,
      tag,
      onClick: () => {
        if (notification.link) {
          window.location.href = notification.link;
        }
      },
    });
  }
}

/**
 * Muestra notificación del navegador para SuperAdmin
 * Personalizada según el tipo de notificación
 * @param {Object} notification - Datos de la notificación SuperAdmin
 */
export function showSuperAdminBrowserNotification(notification) {
  // Verificar permiso
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    console.warn('Notificaciones del navegador no disponibles');
    return null;
  }

  console.log('🔔 [showSuperAdminBrowserNotification] Notificación recibida:', notification);
  console.log('🔔 [showSuperAdminBrowserNotification] Tipo:', notification.type);

  // Mapeo de tipos a emojis y configuración
  const notificationConfig = {
    'new_store_registered': {
      emoji: '🏪',
      title: 'Nueva Tienda Registrada',
      requireInteraction: true
    },
    'trial_expiring': {
      emoji: '⏰',
      title: 'Trial por Vencer',
      requireInteraction: true
    },
    'trial_expired': {
      emoji: '❌',
      title: 'Trial Finalizado',
      requireInteraction: true
    },
    'subscription_expiring': {
      emoji: '⚠️',
      title: 'Suscripción por Vencer',
      requireInteraction: true
    },
    'subscription_expired': {
      emoji: '🛑',
      title: 'Suscripción Expirada',
      requireInteraction: true
    },
    'subscription_payment_uploaded': {
      emoji: '💳',
      title: 'Nuevo Comprobante de Pago',
      requireInteraction: true
    },
    'subscription_activated': {
      emoji: '✅',
      title: 'Suscripción Activada',
      requireInteraction: false
    },
    'subscription_rejected': {
      emoji: '❌',
      title: 'Pago Rechazado',
      requireInteraction: true
    }
  };

  // Obtener configuración según el tipo
  const config = notificationConfig[notification.type] || {
    emoji: '🔔',
    title: 'Notificación',
    requireInteraction: false
  };

  console.log('🔔 [showSuperAdminBrowserNotification] Config encontrada:', config);

  // IMPORTANTE: Usar el título personalizado del config, NO el del notification
  const title = `${config.emoji} ${config.title}`;
  const body = notification.message;
  
  console.log('🔔 [showSuperAdminBrowserNotification] Título final:', title);
  console.log('🔔 [showSuperAdminBrowserNotification] Mensaje:', body);
  const icon = '/icon-192x192.png';
  const badge = '/icon-192x192.png';
  const tag = `superadmin-${notification.type}-${notification.id}`;

  try {
    // IMPORTANTE: Usar Notification API directamente, NO Service Worker
    // El Service Worker solo se usa para notificaciones push con app cerrada
    const browserNotif = new Notification(title, {
      body,
      icon,
      badge,
      tag,
      requireInteraction: config.requireInteraction,
      silent: false,
    });

    console.log('🔔 [showSuperAdminBrowserNotification] Notificación del navegador creada');

    // Manejar click
    browserNotif.onclick = (event) => {
      event.preventDefault();
      window.focus();
      if (notification.link) {
        window.location.href = notification.link;
      }
      browserNotif.close();
    };

    // Auto-cerrar si no requiere interacción
    if (!config.requireInteraction) {
      setTimeout(() => {
        browserNotif.close();
      }, 10000);
    }

    return browserNotif;
  } catch (error) {
    console.error('Error mostrando notificación del navegador:', error);
    return null;
  }
}
